/**
 * Mission File Operations Utility
 *
 * Handles saving and loading missions in JSON format
 */

import type { Mission } from '../types/mission.types';
import type { Waypoint } from '../types/waypoint.types';

/**
 * Mission file format interface
 */
export interface MissionFileFormat {
  fileType: 'KOPASGAT_Mission';
  version: string;
  mission: Mission;
  metadata: {
    createdAt: string;
    application: string;
    applicationVersion: string;
  };
}

/**
 * Serialized waypoint format (for JSON parsing)
 * Dates are serialized as strings in JSON
 */
interface SerializedWaypoint extends Omit<Waypoint, 'createdAt' | 'updatedAt'> {
  createdAt: string;
  updatedAt: string;
}

/**
 * Save mission to JSON file (download to user's computer)
 */
export async function saveMissionToFile(mission: Mission): Promise<void> {
  try {
    // Create file format
    const fileData: MissionFileFormat = {
      fileType: 'KOPASGAT_Mission',
      version: '1.0.0',
      mission,
      metadata: {
        createdAt: new Date().toISOString(),
        application: 'KOPASGAT Drone Planner',
        applicationVersion: '1.0.0',
      },
    };

    // Convert to JSON string with formatting
    const jsonString = JSON.stringify(fileData, null, 2);

    // Create blob
    const blob = new Blob([jsonString], { type: 'application/json' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename: mission-name_YYYYMMDD_HHMMSS.json
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .substring(0, 19);
    const safeName = mission.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    link.download = `${safeName}_${timestamp}.json`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error saving mission to file:', error);
    throw new Error('Failed to save mission file');
  }
}

/**
 * Load mission from JSON file (file picker dialog)
 */
export async function loadMissionFromFile(): Promise<Mission> {
  return new Promise((resolve, reject) => {
    try {
      // Create file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }

        try {
          // Read file content
          const text = await file.text();

          // Parse JSON
          const data = JSON.parse(text) as MissionFileFormat;

          // Validate file format
          if (data.fileType !== 'KOPASGAT_Mission') {
            reject(new Error('Invalid file format'));
            return;
          }

          // Validate version compatibility
          const [major] = data.version.split('.');
          if (major !== '1') {
            reject(new Error(`Unsupported file version: ${data.version}`));
            return;
          }

          // Validate mission data
          if (!data.mission || !data.mission.id || !data.mission.name) {
            reject(new Error('Invalid mission data'));
            return;
          }

          // Convert date strings back to Date objects
          const mission: Mission = {
            ...data.mission,
            createdAt: new Date(data.mission.createdAt),
            updatedAt: new Date(data.mission.updatedAt),
            lastUploadedAt: data.mission.lastUploadedAt
              ? new Date(data.mission.lastUploadedAt)
              : undefined,
            waypoints: data.mission.waypoints.map((wp) => ({
              ...wp,
              createdAt: new Date(wp.createdAt),
              updatedAt: new Date(wp.updatedAt),
            })),
          };

          resolve(mission);
        } catch (error) {
          reject(new Error(`Failed to parse mission file: ${error}`));
        }
      };

      input.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      // Trigger file picker
      input.click();
    } catch (error) {
      reject(new Error(`Failed to load mission file: ${error}`));
    }
  });
}

/**
 * Export mission to JSON string (for clipboard or other uses)
 */
export function exportMissionToJSON(mission: Mission): string {
  const fileData: MissionFileFormat = {
    fileType: 'KOPASGAT_Mission',
    version: '1.0.0',
    mission,
    metadata: {
      createdAt: new Date().toISOString(),
      application: 'KOPASGAT Drone Planner',
      applicationVersion: '1.0.0',
    },
  };

  return JSON.stringify(fileData, null, 2);
}

/**
 * Import mission from JSON string
 */
export function importMissionFromJSON(jsonString: string): Mission {
  try {
    const data = JSON.parse(jsonString) as MissionFileFormat;

    // Validate
    if (data.fileType !== 'KOPASGAT_Mission') {
      throw new Error('Invalid file format');
    }

    // Convert dates
    const mission: Mission = {
      ...data.mission,
      createdAt: new Date(data.mission.createdAt),
      updatedAt: new Date(data.mission.updatedAt),
      lastUploadedAt: data.mission.lastUploadedAt
        ? new Date(data.mission.lastUploadedAt)
        : undefined,
      waypoints: data.mission.waypoints.map((wp) => ({
        ...wp,
        createdAt: new Date(wp.createdAt),
        updatedAt: new Date(wp.updatedAt),
      })),
    };

    return mission;
  } catch (error) {
    throw new Error(`Failed to import mission: ${error}`);
  }
}

/**
 * Auto-save mission to localStorage
 */
export function autoSaveMission(mission: Mission): void {
  try {
    const key = `autosave_mission_${mission.id}`;
    const data = {
      mission,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Auto-save failed:', error);
  }
}

/**
 * Restore mission from auto-save
 */
export function restoreAutoSave(missionId: string): Mission | null {
  try {
    const key = `autosave_mission_${missionId}`;
    const data = localStorage.getItem(key);
    if (!data) return null;

    const parsed = JSON.parse(data);

    // Convert dates
    const mission: Mission = {
      ...parsed.mission,
      createdAt: new Date(parsed.mission.createdAt),
      updatedAt: new Date(parsed.mission.updatedAt),
      lastUploadedAt: parsed.mission.lastUploadedAt
        ? new Date(parsed.mission.lastUploadedAt)
        : undefined,
      waypoints: parsed.mission.waypoints.map((wp: SerializedWaypoint) => ({
        ...wp,
        createdAt: new Date(wp.createdAt),
        updatedAt: new Date(wp.updatedAt),
      })),
    };

    return mission;
  } catch (error) {
    console.error('Restore auto-save failed:', error);
    return null;
  }
}

/**
 * Clear auto-save for a mission
 */
export function clearAutoSave(missionId: string): void {
  try {
    const key = `autosave_mission_${missionId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Clear auto-save failed:', error);
  }
}

/**
 * Get all auto-saved mission IDs
 */
export function getAutoSavedMissionIds(): string[] {
  try {
    const keys = Object.keys(localStorage);
    return keys
      .filter((key) => key.startsWith('autosave_mission_'))
      .map((key) => key.replace('autosave_mission_', ''));
  } catch (error) {
    console.error('Get auto-saved missions failed:', error);
    return [];
  }
}
